var _a;
import { isEqual } from "lodash";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePrevious } from "../../utils/hooks.utils";
import { generateHooks } from "../generators/generator.hooks";
import { getCountStepsStart } from "./application.actions";
import { GENERATOR_PARAMS } from "./application.constants";
import { fetchCountStepsError, getCountSteps, isFetchingCountSteps, } from "./application.selectors";
export var useDetail = (_a = generateHooks(GENERATOR_PARAMS), _a.useDetail), usePaged = _a.usePaged, useUpdate = _a.useUpdate, useCount = _a.useCount, useGetAll = _a.useGetAll, useArchive = _a.useArchive, useUnarchive = _a.useUnarchive;
/**
 * Returns the ids of every application the candidate submitted for the same property. Each
 * re-submission (e.g. visitor → tenant, or editing and re-sending) creates a NEW application
 * row that shares `userId` + `propertyInfoId` with the previous ones. The candidacy history is
 * stored per application, so this lets a history view span all submissions instead of only the
 * latest - otherwise the broker loses everything that happened before the last re-submission.
 *
 * The ids come from `siblingApplicationIds`, which api-next attaches to every application (one
 * batched query per page, alongside the existing cross-property meta). It is populated on both the
 * list and the detail on purpose: the shared redux store overwrites the detail entity with the
 * list version whenever the candidacy list refreshes, so a detail-only field would be dropped and
 * the history would collapse back to the latest submission.
 */
export var getCandidacyApplicationIds = function (application) {
    if (!application) {
        return { applicationIds: [] };
    }
    // Fall back to the current application when the detail predates this field, so history is never
    // empty. `siblingApplicationIds` already includes the current application.
    var applicationIds = application.siblingApplicationIds &&
        application.siblingApplicationIds.length > 0
        ? application.siblingApplicationIds
        : [application.id];
    return { applicationIds: applicationIds };
};
export var useCountSteps = function (params) {
    var query = params.query, shouldRefetch = params.shouldRefetch;
    var prevQuery = usePrevious(query);
    // Redux
    var dispatch = useDispatch();
    var countSteps = useSelector(function (state) { return getCountSteps(state, query); });
    var isFetching = useSelector(function (state) {
        return isFetchingCountSteps(state, query);
    });
    var fetchError = useSelector(function (state) {
        return fetchCountStepsError(state, query);
    });
    // Data
    var startFetching = useCallback(function () {
        if (!query) {
            return;
        }
        if (isFetching) {
            return;
        }
        if (!shouldRefetch && countSteps !== undefined) {
            return;
        }
        if (isEqual(query, prevQuery)) {
            return;
        }
        dispatch(getCountStepsStart.getAction({
            query: query,
        }));
    }, [dispatch, isFetching, query, prevQuery]);
    // Lifecycle
    useEffect(startFetching, [startFetching]);
    return {
        countSteps: countSteps,
        isFetching: isFetching,
        fetchError: fetchError,
    };
};
